import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { User, Plus, Pencil, Trash2, Loader2, Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  type User as UserType,
} from "@api/users";
import { UserForm } from "@components/UserForm";
import { t } from "@lib/i18n";

const PAGE_SIZE = 20;

export function Users() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  const { data: usersData, isLoading, error } = useUsers({
    page,
    page_size: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };
  
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  const handleCreate = (data: Parameters<typeof createMutation.mutate>[0]) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setIsCreating(false);
        // Reset to first page to see the new user
        setPage(1);
      },
    });
  };

  const handleUpdate = (data: Parameters<typeof updateMutation.mutate>[0]["payload"]) => {
    if (editingUser) {
      updateMutation.mutate(
        { id: editingUser.id, payload: data },
        {
          onSuccess: () => {
            setEditingUser(null);
          },
        }
      );
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(t("users.deleteConfirm"))) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <User className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{t("users.title")}</h1>
            <p className="text-sm text-muted-foreground">
              {t("users.description")}
            </p>
          </div>
        </div>
        <Button onClick={() => setIsCreating(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          {t("users.createUser")}
        </Button>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingUser) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingUser ? t("users.editUser") : t("users.createUser")}</CardTitle>
          </CardHeader>
          <CardContent>
            <UserForm
              initialData={editingUser || undefined}
              onSubmit={editingUser ? handleUpdate : handleCreate}
              onCancel={() => {
                setIsCreating(false);
                setEditingUser(null);
              }}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t("users.usersList")}</CardTitle>
            {usersData && (
              <div className="text-sm text-muted-foreground">
                {t("common.showing")} {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, usersData.total)} {t("common.of")} {usersData.total}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("common.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive py-4">
              {t("users.errorLoading")}: {error instanceof Error ? error.message : t("common.unknownError")}
            </div>
          )}

          {!isLoading && !error && usersData && usersData.list.length === 0 && (
            <p className="text-muted-foreground py-4">{t("users.noUsers")}</p>
          )}

          {!isLoading && !error && usersData && usersData.list.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("id")}
                      >
                        {t("common.id")}
                        <SortIcon column="id" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("name")}
                      >
                        {t("users.nameLabel")}
                        <SortIcon column="name" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("email")}
                      >
                        {t("users.emailLabel")}
                        <SortIcon column="email" />
                      </th>
                      <th className="text-right p-2 font-medium">{t("common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData.list.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-muted/50">
                        <td className="p-2">{user.id}</td>
                        <td className="p-2 font-medium">{user.name}</td>
                        <td className="p-2 text-muted-foreground">{user.email}</td>
                        <td className="p-2">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingUser(user)}
                              className="gap-1"
                            >
                              <Pencil className="w-4 h-4" />
                              {t("common.edit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(user.id)}
                              disabled={deleteMutation.isPending}
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t("common.delete")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {usersData.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t("common.page")} {usersData.page} {t("common.of")} {usersData.total_pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1 || isLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t("common.previous")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(usersData.total_pages, p + 1))}
                      disabled={page === usersData.total_pages || isLoading}
                      className="gap-1"
                    >
                      {t("common.next")}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

